/**
 * FinalStage.js
 *
 * Displays the impostors at the end of the game.
 * Clean reveal screen for all players.
 */

import React, { useState } from "react";
import Avatar from "../../../components/ui/Avatar";
import Button from "../../../components/ui/Button";
import { displayResults } from "../helpers/resultHelpers";

/**
 * FinalStage
 *
 * @param {Array} players       - All players with impostor field
 * @param {Object} room         - Room info (for name display)
 * @returns {JSX.Element}       - Final screen showing impostors
 */
const FinalStage = ({ players = [], room, isAdmin }) => {
    const [display, setDisplay] = useState(room?.displayResults || false);
    const impostors = players?.filter((p) => p.impostor);

    const handleRevealResults = async () => {
        const res = await displayResults(room.id);
        if (res)
            setDisplay(true);
    }

    if (!display) {
        return (
            <div className="w-full min-h-screen bg-black text-white flex flex-col items-center justify-center text-center px-6">
                <h2 className="text-3xl font-bold tracking-wide text-zinc-200 mb-4">
                    Final Results
                </h2>

                {isAdmin ? (
                    <>
                        <p className="text-zinc-400 mb-6">
                            Ready to reveal the impostors?
                        </p>
                        <Button
                            variant="secondary"
                            onClick={handleRevealResults}
                            className="px-10 py-2 border border-white rounded-lg hover:bg-white hover:text-black transition-all duration-300"
                        >
                            Continue
                        </Button>
                    </>
                ) : (
                    <p className="text-zinc-500 text-lg">Waiting for Admin...</p>
                )}
            </div>
        );
    }

    return (
        <div className="w-full min-h-screen bg-black px-4 py-6 mt-20 text-white flex flex-col items-center">
            {/* Header */}
            <div className="w-full flex flex-col items-center mb-8">
                <h2 className="text-3xl font-bold text-red-500 tracking-wide text-center drop-shadow-[0_0_10px_rgba(255,0,0,0.6)]">
                    The Impostors Have Been Revealed
                </h2>
                <p className="text-zinc-400 text-sm mt-2">
                    {room?.id ? `Room — ${room.id}` : ""}
                </p>
            </div>

            {/* Impostor List */}
            <div className="w-full flex justify-center">
                <div className="flex flex-col gap-4 w-full max-w-md sm:max-w-lg">
                    {impostors?.length > 0 ? (
                        impostors.map((impostor) => (
                            <div
                                key={impostor.id}
                                className="flex items-center gap-3 p-3 rounded-xl border border-red-600 hover:border-red-400 hover:bg-red-950/20 transition-all duration-200"
                            >
                                <Avatar src={impostor.pfp} alt={impostor.displayName} />
                                <div className="flex flex-col justify-center">
                                    <span className="text-base font-semibold text-red-400 truncate">
                                        {impostor.displayName}
                                    </span>
                                    <span className="text-xs text-zinc-500 italic">
                                        Impostor 🕵️‍♂️
                                    </span>
                                </div>
                            </div>
                        ))
                    ) : (
                        <p className="text-zinc-500 text-center py-6">
                            No impostors found... everyone was too good!
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FinalStage;